function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _inheritsLoose(subClass, superClass) { subClass.prototype = Object.create(superClass.prototype); subClass.prototype.constructor = subClass; subClass.__proto__ = superClass; }

import PropTypes from 'prop-types';
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { findDOMNode } from 'react-dom';
import { ButtonIcon } from '@homeaway/react-buttons';
import { CLOSE__16 } from '@homeaway/svg-defs';
import { TrapFocus } from '@homeaway/a11y-tools';
import classNames from 'classnames';
import TooltipPositioner from './TooltipPositioner';
import { checkElementType } from './utils';
import { addThrottleHandler, resize, scroll } from '@homeaway/raf-optimized';
import "./Tooltip.css";

var Tooltip = /*#__PURE__*/function (_Component) {
  _inheritsLoose(Tooltip, _Component);

  function Tooltip(props) {
    var _this;

    _this = _Component.call(this, props) || this;

    _this.queryFocusableElements = function () {
      var targetContainer = _this.tooltip;
      var tooltipType = _this.props.tooltipType;

      if (targetContainer && tooltipType === 'popover') {
        var node = findDOMNode(targetContainer);

        _this.trapFocus.determineFocusable(node);
      }
    };

    _this.resetFocus = function () {
      _this.queryFocusableElements();

      _this.trapFocus.resetFocus();
    };

    _this.addEventListeners = function () {
      // @homeaway/raf-optimized resize returns a callback to remove the event listener
      // https://github.homeawaycorp.com/traveler/raf-optimized/blob/8f607c662b99f6da33724b3c563e97eba86ef740/src/index.js#L163
      _this.removeResizeEventListener = resize(_this.adjustPositioning);

      if (_this.props.closeOnOutsideClick) {
        window.document.addEventListener('mouseup', _this.handleOutsideMouseClick);
        window.document.addEventListener('touchstart', _this.handleOutsideMouseClick);
      }
    };

    _this.detachEventListeners = function () {
      _this.removeResizeEventListener();

      if (_this.props.closeOnOutsideClick) {
        window.document.removeEventListener('mouseup', _this.handleOutsideMouseClick);
        window.document.removeEventListener('touchstart', _this.handleOutsideMouseClick);
      }

      if (_this.removeNodeScrollListener) {
        _this.removeNodeScrollListener();

        _this.removeNodeScrollListener = null;
      }

      if (_this.removeWindowScrollListener) {
        _this.removeWindowScrollListener();

        _this.removeWindowScrollListener = null;
      }
    };

    _this.adjustPositioning = function () {
      if (_this.tooltip && _this.state.displayTooltip) {
        _this.tooltipPositioner = _this.initTooltipPositioner();
        var tooltipDOM = findDOMNode(_this.tooltip);

        var _this$tooltipPosition = _this.tooltipPositioner.getPositions(),
            tooltipPosition = _this$tooltipPosition.tooltipPosition,
            arrowPosition = _this$tooltipPosition.arrowPosition; // Change className without triggering rerender
        // TODO: refactor this so the render function is the only
        // method setting the className


        tooltipDOM.className = _this.getTooltipClassName(); // Tooltip changes widths after it's repositioned.
        // Force it to stay the same so positioning is correct

        tooltipDOM.style.width = tooltipPosition.width + "px";
        tooltipDOM.style.left = tooltipPosition.left + "px";
        tooltipDOM.style.top = tooltipPosition.top + "px";
        _this.tooltipArrow.style.top = arrowPosition.top;
        _this.tooltipArrow.style.left = arrowPosition.left;
      }
    };

    _this.clearTimeouts = function () {
      if (_this.closeTimeoutId !== null) {
        clearTimeout(_this.closeTimeoutId);
        _this.closeTimeoutId = null;
      }

      if (_this.delayHideTimeoutId !== null) {
        clearTimeout(_this.delayHideTimeoutId);
        _this.delayHideTimeoutId = null;
      }

      if (_this.delayShowTimeoutId !== null) {
        clearTimeout(_this.delayShowTimeoutId);
        _this.delayShowTimeoutId = null;
      }
    };

    _this.handleFocus = function (e) {
      _this.props.onFocus(e);

      _this.handleOpen(e);
    };

    _this.handleOpen = function (e) {
      if (_this.props.trigger === 'none') {
        return;
      }

      var eventType = e.type === 'mouseover' ? 'hover' : e.type; // only trigger openPortal for specified triggers

      if (_this.props.trigger.indexOf(eventType) !== -1) {
        var targetContainer = _this.props.parentNode ? _this.tooltip : _this.container; // close on second click

        if (eventType === 'click' && _this.state.displayTooltip && targetContainer && !targetContainer.contains(e.target)) {
          _this.handleClose(e);

          return;
        } // set up a persist state so only a click can close tooltip


        if (eventType === 'click') {
          _this.setState({
            persist: true
          });
        }

        _this.clearTimeouts();

        _this.delayShowTimeoutId = setTimeout(function () {
          _this.displayTooltip();
        }, _this.props.delayShow);
      }
    };

    _this.handleClose = function (e) {
      if (_this.props.trigger === 'none') {
        return;
      }

      if (!_this.state.persist) {
        _this.clearTimeouts();

        _this.delayHideTimeoutId = setTimeout(function () {
          _this.fadeAndClose();
        }, _this.props.delayHide);
      } else if (e.type === 'click') {
        _this.fadeAndClose();
      }
    };

    _this.handleOutsideMouseClick = function (e) {
      var targetContainer = _this.props.parentNode ? _this.tooltip : _this.container;

      if (_this.state.displayTooltip && _this.props.closeOnOutsideClick && targetContainer && !targetContainer.contains(e.target)) {
        _this.fadeAndClose();
      }
    };

    _this.handleKeydown = function (e) {
      var tooltipType = _this.props.tooltipType;

      if (e.keyCode === _this.KEYCODES.ESCAPE && _this.state.displayTooltip) {
        _this.fadeAndClose();
      } else if (e.keyCode === 9 && tooltipType === 'popover') {
        // Tab key
        _this.trapFocus.handleTabbing(e);
      }
    };

    _this.displayTooltip = function () {
      _this.setState({
        displayTooltip: true
      });

      _this.props.onOpen();
    };

    _this.closeTooltip = function () {
      _this.setState({
        displayTooltip: false
      });

      _this.props.onClose();
    };

    _this.closeTimeoutId = null;
    _this.delayHideTimeoutId = null;
    _this.delayShowTimeoutId = null;
    _this.removeResizeEventListener = Function.prototype; // Noop

    _this.contentId = null;
    _this.trapFocus = new TrapFocus();
    _this.state = {
      persist: _this.props.shouldPersist,
      displayTooltip: false
    };
    _this.KEYCODES = {
      ESCAPE: 27
    };
    return _this;
  }

  var _proto = Tooltip.prototype;

  _proto.componentDidMount = function componentDidMount() {
    this.contentId = Math.floor(Math.random() * 1000);

    if (this.props.isOpen) {
      this.displayTooltip();
    }

    this.addEventListeners();
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps, prevState) {
    var _this$props = this.props,
        isOpen = _this$props.isOpen,
        parentNode = _this$props.parentNode,
        scrollNode = _this$props.scrollNode;
    var displayTooltip = this.state.displayTooltip;

    if (!!prevProps.isOpen !== !!isOpen) {
      if (isOpen) {
        this.displayTooltip();
      } else {
        this.fadeAndClose();
      }
    }

    this.queryFocusableElements();
    var displayChanged = prevState.displayTooltip !== displayTooltip;
    this.adjustPositioning(); // If the tooltip needs to scroll independent of the browser window,
    // listen to scroll events on the specified scrollNode and the browser
    // window to adjust positioning appropriately.

    if (displayChanged && typeof window !== 'undefined' && displayTooltip && parentNode && scrollNode) {
      // Setup listeners when tooltip is displayed.
      this.removeNodeScrollListener = addThrottleHandler('scroll', this.adjustPositioning, scrollNode);
      this.removeWindowScrollListener = scroll(this.adjustPositioning);
    }

    if (displayChanged && !displayTooltip) {
      // Remove listeners when tooltip is hidden.
      if (this.removeNodeScrollListener) {
        this.removeNodeScrollListener();
        this.removeNodeScrollListener = null;
      }

      if (this.removeWindowScrollListener) {
        this.removeWindowScrollListener();
        this.removeWindowScrollListener = null;
      }
    }
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    this.clearTimeouts();
    this.detachEventListeners();
  } // Queries for all focusable elements within the popover container
  ;

  _proto.initTooltipPositioner = function initTooltipPositioner() {
    return new TooltipPositioner({
      fullwidth: this.props.fullwidth,
      parentNode: this.props.parentNode,
      centered: this.props.centered,
      tooltipType: this.props.tooltipType,
      placement: this.props.placement,
      useClientHeight: this.props.useClientHeight
    }, this.container, this.tooltip);
  };

  _proto.getTooltipClassName = function getTooltipClassName() {
    var tooltipType = this.props.tooltipType;
    var placement = this.tooltipPositioner ? this.tooltipPositioner.getSafePlacement() : this.props.placement;
    return classNames(this.props.portalClassName, "Tooltip Tooltip--" + placement, {
      // Checks for tooltip and tooltip-lg
      'Tooltip--popover': tooltipType === 'popover',
      'Tooltip--lg': tooltipType === 'tooltip--lg' || tooltipType === 'popover',
      'Tooltip--full-width': this.props.fullWidth,
      in: this.state.displayTooltip
    }, 'fade');
  };

  /* wait for fade out animation to occur, then remove from DOM */
  _proto.fadeAndClose = function fadeAndClose() {
    var _this2 = this;

    if (!this.props.fadeOut) {
      this.closeTooltip();
    } // The current fade out transition time
    // in Baseline is 350ms so am setting this
    // slightly earlier to prevent being able
    // to mouse over tooltip after it's faded to 0 opacity
    // but still exists in DOM, which causes a flicker.
    // https://github.expedia.biz/vrbo-web-platform/baseline/blob/master/src/less/partials/component-animations.less#L4-L11


    var baselineFadeOutDuration = 350;
    var tooltipDOM = findDOMNode(this.tooltip);

    if (tooltipDOM) {
      tooltipDOM.className = tooltipDOM.className.replace('in ', '');
    }

    this.clearTimeouts();
    this.closeTimeoutId = setTimeout(function () {
      _this2.closeTooltip();
    }, baselineFadeOutDuration - 10);
  };

  // Helper method to render the tooltip content. Separated from the main
  // render method since it can be rendered inside and outside of a portal.
  _proto.renderTooltipContent = function renderTooltipContent(content, title, tooltipType, interactiveContent) {
    var _this3 = this;

    var isPopover = tooltipType === 'popover';
    var role = 'status';
    var innerClassNames = classNames('Tooltip__inner', {
      'text-white': !isPopover
    });

    if (interactiveContent || isPopover) {
      role = 'dialog';
    }

    var props = {
      ref:
      /* istanbul ignore next */
      function ref(c) {
        return _this3.tooltip = c;
      },
      style: {
        display: 'block'
      },
      className: this.getTooltipClassName(),
      role: role,
      onClick: this.props.onClick
    };

    if (this.state.displayTooltip && this.contentId !== null) {
      props.id = "tipContent-" + this.contentId;
      props['aria-live'] = interactiveContent ? 'off' : 'polite';
    }

    return /*#__PURE__*/React.createElement("span", props, /*#__PURE__*/React.createElement("span", {
      className: "Tooltip__arrow",
      ref:
      /* istanbul ignore next */
      function ref(c) {
        return _this3.tooltipArrow = c;
      }
    }), isPopover && title && /*#__PURE__*/React.createElement("span", {
      className: "Tooltip__title"
    }, title), this.props.shouldPersist && isPopover && /*#__PURE__*/React.createElement(ButtonIcon, {
      className: "Tooltip__close",
      label: this.props.labels.close,
      onClick: this.closeTooltip,
      svgMarkup: CLOSE__16,
      size: "xs"
    }), /*#__PURE__*/React.createElement("span", {
      className: innerClassNames
    }, content));
  };

  _proto.render = function render() {
    var _this4 = this;

    var _this$props2 = this.props,
        tooltipType = _this$props2.tooltipType,
        title = _this$props2.title,
        content = _this$props2.content,
        wrappingMethod = _this$props2.wrappingMethod,
        wrapperClassName = _this$props2.wrapperClassName,
        wrapperStyle = _this$props2.wrapperStyle,
        parentNode = _this$props2.parentNode,
        interactiveContent = _this$props2.interactiveContent,
        interactiveContainer = _this$props2.interactiveContainer; // This helps preserve the bounds of the this.props.children

    var wrapStyle = _extends({
      display: wrappingMethod,
      outline: 0,
      position: 'relative'
    }, wrapperStyle);

    var usePortal = parentNode && typeof window !== 'undefined';
    var props = {
      ref:
      /* istanbul ignore next */
      function ref(c) {
        return _this4.container = c;
      },
      className: wrapperClassName,
      style: wrapStyle,
      onMouseOver: this.handleOpen,
      onMouseLeave: this.handleClose,
      onFocus: this.handleFocus,
      onBlur: this.handleClose,
      onClick: this.handleOpen,
      onKeyDown: this.handleKeydown,
      role: interactiveContainer ? 'presentation' : 'button',
      tabIndex: interactiveContainer ? null : '0'
    };

    if (this.state.displayTooltip && this.contentId !== null) {
      props['aria-controls'] = "tipContent-" + this.contentId;
    }

    return /*#__PURE__*/React.createElement("div", props, this.props.children, this.state.displayTooltip && !usePortal && this.renderTooltipContent(content, title, tooltipType, interactiveContent), this.state.displayTooltip && usePortal && ReactDOM.createPortal(this.renderTooltipContent(content, title, tooltipType, interactiveContent), parentNode));
  };

  return Tooltip;
}(Component);

Tooltip.propTypes = {
  /** Delay closing the tooltip in milliseconds */
  delayHide: PropTypes.number,

  /** Delay opening the tooltip in milliseconds */
  delayShow: PropTypes.number,

  /** Whether the tooltip should fade out or removed from the DOM instantly */
  fadeOut: PropTypes.bool,

  /** Content of the Tooltip */
  content: PropTypes.node.isRequired,

  /** If using your own state to control the visibility of the Tooltip, rather than providing an
      openByClickOn element or children, you will set the isOpen property. Also set the trigger to "none".  */
  isOpen: PropTypes.bool,

  /** The function to call when the tooltip closes */
  onClose: PropTypes.func,

  /** Callback for when the tooltip gains focus */
  onFocus: PropTypes.func,

  /** The function to call when the tooltip opens */
  onOpen: PropTypes.func,

  /** The element to attach an event listener that opens the Tooltip */
  openByClickOn: PropTypes.element,

  /** How to position the tooltip */
  placement: PropTypes.oneOf(['top', 'left', 'bottom', 'right']),

  /** Whether to center the tooltip to its content or allow for the default offset */
  centered: PropTypes.bool,

  /** The DOM element to use as the parent for the tooltip when visible. This is useful in scenarios where the tooltip needs to break out of its current container due to overflow or z-index issues. */
  parentNode: checkElementType,

  /** Allows you to add a classname to the popover/tooltip for custom styling */
  portalClassName: PropTypes.string,

  /** The DOM element that the tooltip needs to scroll with. Only specify this if parentNode was specified and the tooltip is displayed in a container that is scrollable independent of the browser window. */
  scrollNode: checkElementType,

  /** Tooltip Title, used only in tooltipType === 'popover' */
  title: PropTypes.node,

  /** Type of Tooltip (Tooltip, Popover, Large Tooltip) to display */
  tooltipType: PropTypes.oneOf(['tooltip', 'popover', 'tooltip--lg']),

  /** How tooltip is triggered: click | hover | focus | none. You may pass multiple triggers; separate them with a space. Set this to "none" if you are controlling the tooltip/popover visibility externally using the "isOpen" property. */
  trigger: PropTypes.string,

  /** className prop to pass to the wrapper div. Defaults to an empty string. */
  wrapperClassName: PropTypes.string,

  /** style prop to pass to the wrapper div. Defaults to an empty object. */
  wrapperStyle: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types

  /** If the element that you are applying the tooltip to is a block element, switch the wrappingMethod from its default to 'block'.
   * This prop is left for compatibility with older versions and the display value may be overridden with `wrapperStyle` instead. */
  wrappingMethod: PropTypes.oneOf(['inline-block', 'block']),

  /** Boolean to determine whether the tooltip will close if the user clicks outside of the tooltip. */
  closeOnOutsideClick: PropTypes.bool,

  /** Flag to use when Tooltip content contains elements that are interactive. This will remove the default `aria-live` attribute and `tabindex`. As a result, it will prevent aria-live from causing a screen reader to read out all elements within a Tooltip (ex: a calendar) when it opens. Defaults to false.*/
  interactiveContent: PropTypes.bool,

  /** Flag to use when a Tooltip is used on an element that can receive its own focus. This will remove the `role="button"` attribute and `tabindex` from the span wrapper. Ie: When using a Button, FormInput, etc. */
  interactiveContainer: PropTypes.bool,

  /** Flag to use when a tooltip should match the width of the element that it is attached to. Defaults to false. */
  fullWidth: PropTypes.bool,

  /** Force using window bounds (instead of body) for popovers. Defaults to false. */
  useClientHeight: PropTypes.bool,

  /** Should persist until closed. Implies close button is visible. */
  shouldPersist: PropTypes.bool,

  /** Localized labels */
  labels: PropTypes.shape({
    close: PropTypes.string
  })
};
Tooltip.defaultProps = {
  content: '',
  delayHide: 0,
  delayShow: 0,
  fadeOut: true,
  isOpen: false,
  onClose: function onClose() {},
  onFocus: function onFocus() {},
  onOpen: function onOpen() {},
  placement: 'top',
  centered: false,
  tooltipType: 'tooltip',
  trigger: 'hover focus',
  wrapperClassName: '',
  wrapperStyle: {},
  wrappingMethod: 'inline-block',
  closeOnOutsideClick: true,
  interactiveContent: false,
  interactiveContainer: false,
  fullWidth: false,
  useClientHeight: false,
  hasCloseButton: false,
  labels: {
    close: 'Close'
  }
};
export default Tooltip;
//# sourceMappingURL=Tooltip.js.map